import { Injectable } from '@angular/core';
import { ISeason } from '@api/models/season/season';

@Injectable({
  providedIn: 'root',
})
export class LibUtilsService {
  public static getTimestampFromDaysAhead(days: number): number {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + days);
    targetDate.setHours(23, 59, 59, 999);
    return targetDate.getTime();
  }

  public static getTimestampFromDaysAgo(days: number): number {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() - days);
    targetDate.setHours(0, 0, 0, 0);
    return targetDate.getTime();
  }

  public static getCurrentMondayTime() {
    const today = new Date();
    // Get current day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const currentDay = today.getDay();
    const prevMonday = new Date(today);
    console.log(currentDay, today.getDate());
    prevMonday.setDate(today.getDate() - currentDay + 1);
    prevMonday.setHours(0, 0, 0, 0);
    return prevMonday.getTime();
  }

  public static getCurrentSundayTime() {
    const previousMonday = new Date(this.getCurrentMondayTime());
    const previousSunday = new Date(previousMonday);
    previousSunday.setDate(previousMonday.getDate() + 6);
    previousSunday.setHours(23, 59, 59, 999);
    return previousSunday.getTime();
  }

  public static getPreviousMondayTime() {
    const today = new Date();
    // Get current day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const currentDay = today.getDay();
    const prevMonday = new Date(today);
    prevMonday.setDate(today.getDate() - (currentDay + 6));
    prevMonday.setHours(0, 0, 0, 0);
    return prevMonday.getTime();
  }

  public static getPreviousSundayTime() {
    const previousMonday = new Date(this.getPreviousMondayTime());
    const previousSunday = new Date(previousMonday);
    previousSunday.setDate(previousMonday.getDate() + 6);
    previousSunday.setHours(23, 59, 59, 999);
    return previousSunday.getTime();
  }

  public static getNextMondayTime() {
    const now = new Date();
    const nextMonday = new Date(now);
    // If today is Monday (1), we want to go back 7 days
    // Otherwise, calculate days to previous Monday
    const daysToAdd = now.getDay() === 1 ? 7 : (1 + 7 - now.getDay()) % 7;
    nextMonday.setDate(now.getDate() + daysToAdd);
    nextMonday.setHours(0, 0, 0, 0);
    return nextMonday.getTime();
  }

  public static getNextSundayTime() {
    const nextMonday = new Date(this.getNextMondayTime());
    const nextSunday = new Date(nextMonday);
    nextSunday.setDate(nextMonday.getDate() + 6);
    nextSunday.setHours(23, 59, 59, 999);
    return nextSunday.getTime();
  }

  public static getCalendarWeek(date: number): string {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const week1 = new Date(d.getFullYear(), 0, 4);
    return (
      1 +
      Math.round(
        ((d.getTime() - week1.getTime()) / 86400000 -
          3 +
          ((week1.getDay() + 6) % 7)) /
          7
      )
    ).toString();
  }

  public static getMB(byteSize: number): number {
    return byteSize / 1000 / 1000;
  }

  public static isYesterday(timestamp: number): boolean {
    const date = new Date(timestamp).setHours(0, 0, 0, 0);
    const today = new Date();
    const yesterday = new Date(today.setDate(today.getDate() - 1)).setHours(
      0,
      0,
      0,
      0
    );
    return date === yesterday;
  }

  public static isToday(timestamp: number): boolean {
    const date = new Date(timestamp).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    return date === today;
  }

  public static isTomorrow(timestamp: number): boolean {
    const date = new Date(timestamp).setHours(0, 0, 0, 0);
    const today = new Date();
    const tomorrow = new Date(today.setDate(today.getDate() + 1)).setHours(
      0,
      0,
      0,
      0
    );
    return date === tomorrow;
  }

  public static isHexColorDark(hexColor: string): boolean {
    // Remove alpha channels
    if (hexColor.length > 7) {
      hexColor = hexColor.slice(0, 7);
    }
    const rgb = this.hexToRgb(hexColor);
    const hsl = this.rgbToHsl(rgb[0], rgb[1], rgb[2]);
    const lightness = hsl[2];
    return lightness < 0.5;
  }

  public static hexToRgb(hex: string): number[] {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16),
        ]
      : null;
  }

  public static rgbToHsl(r: number, g: number, b: number): number[] {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = (max + min) / 2;
    let s: number;
    const l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }
    return [h, s, l];
  }

  /**
   * Outputs a short date string with the format:
   * Yesterday: "Gestern 20:00 Uhr"
   * Today: "Heute 20:00 Uhr"
   * Tomorrow: "Morgen 20:00 Uhr"
   * Default: "19.06. 20:00 Uhr"
   * @param timestamp to get the date string from
   * @param withTime should the time be added to the returned string?
   */
  public static getShortDateString(
    timestamp: number,
    withTime = true,
    withYear = true
  ): string {
    if (!timestamp) {
      return '';
    }
    const date = new Date(timestamp);
    const pad2 = LibUtilsService.pad2;
    const time = ` ${pad2(date.getHours())}:${pad2(date.getMinutes())} Uhr`;
    let shortDate = `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.`;
    if (new Date().getFullYear() !== date.getFullYear() && withYear) {
      shortDate += `${date.getFullYear()}`;
    }
    if (LibUtilsService.isYesterday(timestamp)) {
      shortDate = 'Gestern';
    }
    if (LibUtilsService.isToday(timestamp)) {
      shortDate = 'Heute';
    }
    if (LibUtilsService.isTomorrow(timestamp)) {
      shortDate = 'Morgen';
    }
    return shortDate + (withTime ? time : '');
  }

  public static getActiveSeason(seasons: ISeason[]): ISeason | null {
    const currentTime = Date.now();
    let currentSeason: ISeason;
    seasons.forEach((season) => {
      if (
        currentTime > season.season_start &&
        currentTime < season.season_end
      ) {
        currentSeason = season;
      }
    });
    return currentSeason || seasons[0];
  }

  /**
   * Adds additional zero to single numbers ( 9 -> 09 )
   */
  public static pad2(n) {
    return n < 10 ? '0' + n : n;
  }

  public static flattenObject(ob, prefix?: string, result = null) {
    result = result || {};

    // Preserve empty objects and arrays, they are lost otherwise
    if (
      prefix &&
      typeof ob === 'object' &&
      ob !== null &&
      Object.keys(ob).length === 0
    ) {
      result[prefix] = Array.isArray(ob) ? [] : {};
      return result;
    }

    prefix = prefix ? prefix + '.' : '';

    for (const i in ob) {
      if (Object.prototype.hasOwnProperty.call(ob, i)) {
        // Only recurse on true objects and arrays, ignore custom classes like dates
        if (
          typeof ob[i] === 'object' &&
          (Array.isArray(ob[i]) ||
            Object.prototype.toString.call(ob[i]) === '[object Object]') &&
          ob[i] !== null
        ) {
          // Recursion on deeper objects
          this.flattenObject(ob[i], prefix + i, result);
        } else {
          let newPrefix = prefix + i;
          const parts = newPrefix.split('.');
          if (parts.length > 3) {
            newPrefix = `${parts[2]}.${parts[3]}`;
          }
          result[newPrefix] = ob[i];
        }
      }
    }
    return result;
  }
}
